import { useState, useEffect, useCallback, useRef } from 'react';
import { api, API_BASE_URL, tenantHeaders } from '../config/api';
import { EnhancedChatMessage, StudySession } from '../types/educational-chat';

/**
 * Video lecture chat — the SSE client for the watch-page Chat tab.
 *
 * Mirrors useGeneralChat's RETURN SHAPE so the same book-chat UI components
 * (ChatMessage, ChatInput, ChatSidebar, ChatSessionHeader) compose over it
 * unchanged. The only differences are video-specific:
 *   - sessions are session_type='video_chat', scoped to THIS lecture via
 *     session_metadata.video_id (so each lecture has its own conversation list);
 *   - every turn sends video_id + playhead_seconds, so the agent knows which
 *     lecture and where the student is paused (deixis anchor).
 */
export interface VideoChatAttachment {
  type: 'image';
  data_url: string;
  file_name: string;
  mime_type: string;
}

export interface UseVideoChatReturn {
  messages: EnhancedChatMessage[];
  sessions: StudySession[];
  activeSessionId: string | null;
  isLoading: boolean;
  isStreaming: boolean;
  streamingMessageId: string | null;
  agentStatus: string;
  error: string | null;
  sendMessage: (text: string, playheadSeconds: number, attachments?: VideoChatAttachment[]) => Promise<void>;
  createNewSession: () => void;
  selectSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  renameSession: (sessionId: string, newTitle: string) => Promise<void>;
  stopStreaming: () => void;
}

export const useVideoChat = (videoId: string): UseVideoChatReturn => {
  const [messages, setMessages] = useState<EnhancedChatMessage[]>([]);
  const [sessions, setSessions] = useState<StudySession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [agentStatus, setAgentStatus] = useState('');
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  // Real-time mirror of activeSessionId for the stream handler / send path.
  const activeSessionIdRef = useRef<string | null>(null);
  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);
  // Live mirror of the session list so create paths can name "Chat N" without
  // adding `sessions` to their dependency arrays.
  const sessionsRef = useRef<StudySession[]>([]);
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);
  // Guards "New chat" against a double-click creating two empty sessions.
  const creatingRef = useRef(false);

  const loadSessionMessages = useCallback(async (sessionId: string) => {
    try {
      setIsLoading(true);
      const res = await api.get(`/api/chat/sessions/${sessionId}/messages`);
      setMessages((res.data || []).map((m: any) => ({
        id: m.id,
        session_id: m.session_id,
        message_type: m.message_type,
        content_text: m.content_text,
        citations: m.citations || [],
        attachments: m.attachments || [],
        created_at: m.created_at,
      })));
    } catch (err) {
      console.error('[VideoChat] Failed to load messages:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // On mount / video change: load THIS lecture's conversations + resume the latest.
  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    (async () => {
      try {
        // Scope to THIS lecture server-side — the list endpoint is limit-capped, so
        // filtering client-side would drop this video's sessions once the student has
        // chatted on enough other lectures to fill the cap.
        const res = await api.get('/api/chat/sessions', { params: { session_type: 'video_chat', video_id: videoId } });
        const mine: StudySession[] = (res.data?.sessions || [])
          .map((s: any) => ({
            id: s.id,
            title: s.title || s.session_name,
            session_type: s.session_type,
            message_count: 0,
            last_activity: s.updated_at || s.created_at,
            created_at: s.created_at,
          }));
        if (cancelled) return;
        setSessions(mine);
        if (mine.length > 0) {
          setActiveSessionId(mine[0].id);
          await loadSessionMessages(mine[0].id);
        }
      } catch (err) {
        console.error('[VideoChat] Failed to load sessions:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [videoId, loadSessionMessages]);

  // Eagerly create a fresh video_chat session (like the reader's "New chat"):
  // POST right away, add it to the list, make it active, and clear the view —
  // so a new conversation exists immediately, before the first message.
  const createNewSession = useCallback(async () => {
    if (creatingRef.current) return; // a double-click must not create two sessions
    creatingRef.current = true;
    setMessages([]);
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    setError(null);
    setAgentStatus('');
    try {
      const name = `Chat ${sessionsRef.current.length + 1}`;
      const res = await api.post('/api/chat/sessions', {
        session_name: name,
        session_type: 'video_chat',
        session_metadata: { video_id: videoId },
      });
      const id = res.data.id;
      setSessions((prev) => [{
        id,
        title: name,
        session_type: 'video_chat',
        message_count: 0,
        last_activity: new Date().toISOString(),
        created_at: new Date().toISOString(),
      } as StudySession, ...prev]);
      setActiveSessionId(id);
      activeSessionIdRef.current = id;
    } catch (err) {
      console.error('[VideoChat] Failed to create session:', err);
    } finally {
      creatingRef.current = false;
    }
  }, [videoId]);

  const selectSession = useCallback(async (sessionId: string) => {
    setActiveSessionId(sessionId);
    setError(null);
    await loadSessionMessages(sessionId);
  }, [loadSessionMessages]);

  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await api.delete(`/api/chat/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (sessionId === activeSessionIdRef.current) {
        setMessages([]);
        setActiveSessionId(null);
      }
    } catch (err) {
      console.error('[VideoChat] Failed to delete session:', err);
    }
  }, []);

  const renameSession = useCallback(async (sessionId: string, newTitle: string) => {
    try {
      await api.patch(`/api/chat/sessions/${sessionId}`, { session_name: newTitle });
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title: newTitle } : s));
    } catch (err) {
      console.error('[VideoChat] Failed to rename session:', err);
    }
  }, []);

  const sendMessage = useCallback(async (text: string, playheadSeconds: number, attachments?: VideoChatAttachment[]) => {
    if ((!text.trim() && !attachments?.length) || isStreaming) return;

    try {
      setIsLoading(true);
      setIsStreaming(true);
      setError(null);
      setAgentStatus('');

      let sessionId = activeSessionIdRef.current ?? '';

      // Create a video_chat session scoped to this lecture on first message
      // (when the student typed straight into a fresh chat without clicking New).
      if (!sessionId) {
        const name = `Chat ${sessionsRef.current.length + 1}`;
        const sres = await api.post('/api/chat/sessions', {
          session_name: name,
          session_type: 'video_chat',
          session_metadata: { video_id: videoId },
        });
        sessionId = sres.data.id;
        setActiveSessionId(sessionId);
        activeSessionIdRef.current = sessionId;
        setSessions(prev => [{
          id: sessionId,
          title: name,
          session_type: 'video_chat',
          message_count: 0,
          last_activity: new Date().toISOString(),
          created_at: new Date().toISOString(),
        } as StudySession, ...prev]);
      }

      // Optimistic user + AI placeholder.
      const userMessage: EnhancedChatMessage = {
        id: `temp-${Date.now()}`,
        session_id: sessionId,
        message_type: 'user',
        content_text: text,
        attachments: attachments || [],
        created_at: new Date().toISOString(),
      };
      let aiMessageId = `temp-${Date.now() + 1}`;
      setMessages(prev => [...prev, userMessage, {
        id: aiMessageId,
        session_id: sessionId,
        message_type: 'ai',
        content_text: '',
        citations: [],
        created_at: new Date().toISOString(),
      }]);
      setStreamingMessageId(aiMessageId);

      abortControllerRef.current = new AbortController();
      const response = await fetch(`${API_BASE_URL}/api/agent-chat/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          ...tenantHeaders(),
        },
        body: JSON.stringify({
          query: text,
          session_id: sessionId,
          current_page: 1, // unused for video, but the request schema requires it
          video_id: videoId,
          playhead_seconds: Math.max(0, Math.floor(playheadSeconds || 0)),
          images: attachments || [], // photographed problem/diagram → image_normalizer
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to get a response');
      }
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let firstToken = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          if (line.startsWith('event: ')) { currentEvent = line.slice(7).trim(); continue; }
          if (!line.startsWith('data: ')) continue;

          try {
            const data = JSON.parse(line.slice(6));

            if (currentEvent === 'status') {
              setAgentStatus(data.message || '');
            } else if (currentEvent === 'content') {
              if (!firstToken) { setIsLoading(false); firstToken = true; }
              setMessages(prev => prev.map(m =>
                m.id === aiMessageId ? { ...m, content_text: m.content_text + (data.text || '') } : m
              ));
            } else if (currentEvent === 'title') {
              if (data.title && sessionId) {
                api.patch(`/api/chat/sessions/${sessionId}`, { title: data.title }).catch(() => {});
                setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title: data.title } : s));
              }
            } else if (currentEvent === 'user_message') {
              if (data.id) {
                setMessages(prev => prev.map(m => m.id === userMessage.id ? { ...m, id: data.id } : m));
              }
            } else if (currentEvent === 'ai_message') {
              if (data.id) {
                const old = aiMessageId;
                aiMessageId = data.id;
                setStreamingMessageId(data.id);
                setMessages(prev => prev.map(m => m.id === old ? { ...m, id: data.id } : m));
              }
            } else if (currentEvent === 'error') {
              const msg = data.message || 'Something went wrong while answering.';
              setError(msg);
              setMessages(prev => prev.map(m => m.id === aiMessageId ? { ...m, content_text: msg } : m));
            } else if (currentEvent === 'done') {
              // Safety net: pull the persisted message (final citations / clean text).
              if (aiMessageId && !aiMessageId.startsWith('temp-')) {
                try {
                  const r = await api.get(`/api/chat/messages/${aiMessageId}`);
                  setMessages(prev => prev.map(m => m.id === aiMessageId
                    ? { ...m, content_text: r.data.content_text, citations: r.data.citations || [] }
                    : m));
                } catch { /* streamed content stays */ }
              }
              break;
            }
          } catch (err) {
            console.error('[VideoChat] SSE parse error:', err, line);
          }
          currentEvent = '';
        }
      }

      setSessions(prev => prev.map(s => s.id === sessionId
        ? { ...s, last_activity: new Date().toISOString() } : s));
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setAgentStatus('Stopped');
      } else {
        console.error('[VideoChat] Failed to send message:', err);
        setError(err?.message || 'Failed to send message. Please try again.');
        // Drop the empty AI placeholder if nothing streamed.
        setMessages(prev => {
          const lastEmpty = [...prev].reverse().find(m => m.message_type === 'ai' && !m.content_text);
          return lastEmpty ? prev.filter(m => m.id !== lastEmpty.id) : prev;
        });
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingMessageId(null);
      setAgentStatus('');
      abortControllerRef.current = null;
      readerRef.current = null;
    }
  }, [videoId, isStreaming]);

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
    readerRef.current?.cancel().catch(() => {});
    abortControllerRef.current = null;
    readerRef.current = null;
    setIsStreaming(false);
    setStreamingMessageId(null);
    setAgentStatus('');
  }, []);

  useEffect(() => () => { abortControllerRef.current?.abort(); }, []);

  return {
    messages,
    sessions,
    activeSessionId,
    isLoading,
    isStreaming,
    streamingMessageId,
    agentStatus,
    error,
    sendMessage,
    createNewSession,
    selectSession,
    deleteSession,
    renameSession,
    stopStreaming,
  };
};

export default useVideoChat;
